// resources/js/src/services/post.api.ts
import { request, requestFull } from "./api";
import type { ReactionType } from "../components/community/components/post/interfaces/types";

// ============ Types ============

export type PostMedia = {
  id: number;
  type: "image" | "video";
  url: string;
  mimeType: string | null;
  size: number | null;
  sortOrder: number;
};

export type PostReaction = {
  id: number;
  type: ReactionType;
  user: {
    id: number;
    name: string;
    avatarUrl: string | null;
  };
};

export type CommunityPost = {
  id: number;
  body: string | null;
  visibility: "public" | "friends" | "only_me";
  status: string;

  author: {
    id: number;
    name: string;
    avatarUrl: string | null;
  };

  isOwner: boolean;

  // ✅ Reactions
  reactions: PostReaction[];
  reactionsCount: number;
  myReaction: ReactionType;

  // ✅ Comments
  commentsCount: number;

  // ✅ Media
  media: PostMedia[];
  mediaCount: number;

  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type FeedResponse = {
  data: CommunityPost[];
  meta: {
    per_page: number;
    next_cursor: string | null;
    prev_cursor: string | null;
    has_more: boolean;
  };
};

export type CreatePostPayload = {
  body?: string;
  visibility?: "public" | "friends" | "only_me";
  media?: File[];
};

export type UpdatePostPayload = {
  body?: string;
  visibility?: "public" | "friends" | "only_me";
  media?: File[];
  remove_media?: number[];
};

export type ReactionResponse = {
  myReaction: ReactionType;
  reactionsCount: number;
  reactions: PostReaction[];
};

// ============ API ============

export const postApi = {
  loadFeed: (cursor?: string | null, perPage = 15) => {
    const params = new URLSearchParams();
    params.set("per_page", String(perPage));
    if (cursor) params.set("cursor", cursor);

    return requestFull<FeedResponse>(
      "GET",
      `/community/posts?${params.toString()}`
    );
  },

  loadMyPosts: (cursor?: string | null, perPage = 15) => {
    const params = new URLSearchParams();
    params.set("per_page", String(perPage));
    if (cursor) params.set("cursor", cursor);

    return requestFull<FeedResponse>(
      "GET",
      `/community/posts/mine?${params.toString()}`
    );
  },

  showPost: (id: number | string) =>
    request<CommunityPost>("GET", `/community/posts/${id}`),

  createPost: (payload: CreatePostPayload) => {
    const formData = new FormData();

    if (payload.body) formData.append("body", payload.body);
    if (payload.visibility) formData.append("visibility", payload.visibility);

    payload.media?.forEach((file) => {
      formData.append("media[]", file);
    });

    return request<CommunityPost>("POST", "/community/posts", formData);
  },

  updatePost: (id: number | string, payload: UpdatePostPayload) => {
    const formData = new FormData();

    formData.append("_method", "PATCH");

    if (payload.body !== undefined) formData.append("body", payload.body);
    if (payload.visibility) formData.append("visibility", payload.visibility);

    payload.media?.forEach((file) => {
      formData.append("media[]", file);
    });

    payload.remove_media?.forEach((mediaId) => {
      formData.append("remove_media[]", String(mediaId));
    });

    return request<CommunityPost>("POST", `/community/posts/${id}`, formData);
  },

  deletePost: (id: number | string) =>
    request<null>("DELETE", `/community/posts/${id}`),

  // ✅ Reactions
  reactToPost: (id: number | string, reaction: ReactionType) =>
    request<ReactionResponse>(
      "POST",
      `/community/posts/${id}/reactions`,
      { reaction }
    ),
};